import { Add } from "iconsax-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CMSFilterBar } from "../components/CMSFilterBar";
import { CMSPagination } from "../components/CMSPagination";
import { CMSTable } from "../components/CMSTable";
import { CMSSheet } from "../components/CMSSheet";
import { ProductForm } from "../components/ProductForm";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { DateFilterModal } from "../components/DateFilterModal";
import instance from "../../../../axios/instance";

const statuses = ["All Status", "published", "draft", "archived"];

export default function ProductsPages() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [paginatedProducts, setPaginatedProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const itemsPerPage = 10;
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    product: null,
  });
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    shortDescription: "",
    heroDescription: "",
    aboutProduct: "",
    heroImage: null,
    benefitImage: null,
    benefits: [{ title: "", description: "" }],
    faqs: [{ question: "", answer: "" }],
  });

  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await instance.get("/doctor/cms/products", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      // toast.error("Failed to load products. Please try again.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = products;

    if (searchQuery) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (selectedStatus !== "All Status") {
      filtered = filtered.filter(
        (product) => product.status === selectedStatus,
      );
    }

    // Date range filter
    if (dateRange.startDate && dateRange.endDate) {
      filtered = filtered.filter((product) => {
        const productDate = new Date(product.created_at);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        return productDate >= start && productDate <= end;
      });
    }

    setFilteredProducts(filtered);
    setPageIndex(1);
  }, [searchQuery, selectedStatus, dateRange, products]);

  useEffect(() => {
    const startIndex = (pageIndex - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedProducts(filteredProducts.slice(startIndex, endIndex));
  }, [filteredProducts, pageIndex]);

  const handleAction = async (action, product) => {
    setOpenDropdown(null);

    switch (action) {
      case "publish":
        await handleStatusChange(product.id, "published");
        break;
      case "unpublish":
        await handleStatusChange(product.id, "draft");
        break;
      case "edit":
        handleEditProduct(product);
        break;
      case "delete":
        setDeleteModal({ isOpen: true, product });
        break;
      default:
        break;
    }
  };

  const handleStatusChange = async (id, status) => {
    setLoading(true);
    try {
      await instance.put(
        `/doctor/cms/products/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchProducts();
      toast.success(`Product status updated to ${status}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update product status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      name: "",
      slug: "",
      shortDescription: "",
      heroDescription: "",
      aboutProduct: "",
      heroImage: null,
      benefitImage: null,
      benefits: [{ title: "", description: "" }],
      faqs: [{ question: "", answer: "" }],
    });
    setIsSheetOpen(true);
  };

  const handleEditProduct = async (product) => {
    setLoading(true);
    try {
      const response = await instance.get(
        `/doctor/cms/products/${product.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const productData = response.data;
      setIsEditing(true);
      setEditingId(product.id);

      // Get base URL for image previews
      const baseURL = instance.defaults.baseURL || "";

      setFormData({
        name: productData.name || "",
        slug: productData.slug || "",
        shortDescription: productData.short_description || "",
        heroDescription: productData.hero_description || "",
        aboutProduct: productData.about_product || "",
        heroImage: productData.hero_image_path
          ? {
            preview: `${baseURL}${productData.hero_image_path}`,
            name: "Current image",
            isExisting: true,
          }
          : null,
        benefitImage: productData.benefit_image_path
          ? {
            preview: `${baseURL}${productData.benefit_image_path}`,
            name: "Current image",
            isExisting: true,
          }
          : null,
        benefits: Array.isArray(productData.benefits)
          ? productData.benefits
          : productData.benefits
            ? JSON.parse(productData.benefits)
            : [{ title: "", description: "" }],
        faqs: Array.isArray(productData.faqs)
          ? productData.faqs
          : productData.faqs
            ? JSON.parse(productData.faqs)
            : [{ question: "", answer: "" }],
      });
      setIsSheetOpen(true);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSaveProduct = async () => {
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      const dataObject = {
        name: formData.name,
        slug: formData.slug,
        shortDescription: formData.shortDescription,
        heroDescription: formData.heroDescription,
        aboutProduct: formData.aboutProduct,
        benefits: formData.benefits,
        faqs: formData.faqs,
      };

      formDataToSend.append("formData", JSON.stringify(dataObject));

      if (formData.heroImage && formData.heroImage.file) {
        formDataToSend.append("heroImage", formData.heroImage.file);
      }
      if (formData.benefitImage && formData.benefitImage.file) {
        formDataToSend.append("benefitImage", formData.benefitImage.file);
      }

      if (isEditing) {
        await instance.put(
          `/doctor/cms/products/${editingId}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        await instance.post("/doctor/cms/products", formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      // Reset form
      setFormData({
        name: "",
        slug: "",
        shortDescription: "",
        heroDescription: "",
        aboutProduct: "",
        heroImage: null,
        benefitImage: null,
        benefits: [{ title: "", description: "" }],
        faqs: [{ question: "", answer: "" }],
      });

      setIsSheetOpen(false);
      setIsEditing(false);
      setEditingId(null);
      await fetchProducts();
      toast.success(
        isEditing
          ? "Product updated successfully!"
          : "Product created successfully!",
      );
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to save product. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);

    try {
      const product = deleteModal.product;

      if (product.status === "archived") {
        await instance.delete(`/doctor/cms/products/${product.id}/permanent`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await instance.delete(`/doctor/cms/products/${product.id}/soft`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setDeleteModal({ isOpen: false, product: null });
      await fetchProducts();
      toast.success(
        product.status === "archived"
          ? "Product permanently deleted!"
          : "Product archived successfully!",
      );
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to delete product. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.name &&
    formData.slug &&
    formData.shortDescription &&
    formData.heroDescription &&
    formData.aboutProduct &&
    (!isEditing || formData.heroImage || formData.benefitImage !== null) &&
    (isEditing || (formData.heroImage && formData.benefitImage));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns = [
    { key: "name", label: "Name", align: "left" },
    { key: "treatments", label: "Treatments", align: "left" },
    { key: "status", label: "Status", align: "left" },
    { key: "updated", label: "Updated", align: "left" },
    { key: "actions", label: "", align: "center" },
  ];

  const actions = [
    {
      key: "publish",
      label: "Publish",
      variant: "success",
      condition: (item) => item.status !== "published",
    },
    {
      key: "unpublish",
      label: "Unpublish",
      variant: "warning",
      condition: (item) => item.status === "published",
    },
    {
      key: "edit",
      label: "Edit",
      variant: "primary",
    },
    {
      key: "delete",
      label: (product) =>
        product.status === "archived" ? "Delete Permanently" : "Archive",
      variant: "danger",
    },
  ];

  const renderCell = (key, item) => {
    switch (key) {
      case "name":
        return (
          <div className="text-[14px] font-medium text-gray-900">
            {item.name}
          </div>
        );
      case "treatments":
        return (
          <div className="text-[14px] text-gray-600">
            {item.treatments || 0}
          </div>
        );
      case "status":
        return (
          <span
            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium capitalize ${item.status === "published"
                ? "bg-green-100 text-green-800"
                : item.status === "draft"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
              }`}
          >
            {item.status}
          </span>
        );
      case "updated":
        return (
          <div className="text-[14px] text-gray-600">
            {formatDate(item.updated_at || item.created_at)}
          </div>
        );
      default:
        return <div className="text-[14px] text-gray-900">{item[key]}</div>;
    }
  };

  return (
    <div className="bg-[#fafafa] min-h-screen">
      <div className="max-w-[100%] w-full px-4 lg:px-7 py-5">
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-2">
            <div className="mb-2 lg:mb-0">
              <h1 className="text-2xl font-bold text-gray-900">
                Products Pages
              </h1>
              <p className="text-gray-600 text-[14px] mt-1 max-w-2xl">
                Manage product categories that organize your treatments.
              </p>
            </div>
            <button
              onClick={handleOpenSheet}
              className="bg-primary py-2.5 px-4 rounded-md font-medium text-[14px] hover:opacity-80 text-white flex items-center gap-2 transition-all duration-300"
            >
              <Add size={18} />
              New Product
            </button>
          </div>
        </div>

        <CMSFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search products..."
          statuses={statuses}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          showDateFilter={true}
          onDateClick={() => setIsDateFilterOpen(true)}
        />

        <CMSTable
          columns={columns}
          data={paginatedProducts}
          loading={loading}
          emptyMessage="No products found"
          onActionClick={handleAction}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          actions={actions}
          renderCell={renderCell}
        />

        {!loading && (
          <CMSPagination
            currentPage={pageIndex}
            totalItems={filteredProducts.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setPageIndex}
            itemName="products"
            showingCount={paginatedProducts.length}
          />
        )}
      </div>

      <CMSSheet
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        onCancel={handleCloseSheet}
        onSave={handleSaveProduct}
        title={isEditing ? "Edit Product" : "New Product"}
        desc={"Manage product categories that organize your treatments."}
        saveButtonText={isEditing ? "Update" : "Create"}
        cancelButtonText="Cancel"
        isLoading={loading}
        isSaveDisabled={!isFormValid}
      >
        <ProductForm formData={formData} onChange={setFormData} />
      </CMSSheet>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, product: null })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.product?.name}
        isLoading={loading}
        message={
          deleteModal.product?.status === "archived"
            ? "This will permanently delete the product. This action cannot be undone."
            : "This will archive the product. You can permanently delete it later from the archived items."
        }
      />

      <DateFilterModal
        isOpen={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        onApply={() => setIsDateFilterOpen(false)}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
    </div>
  );
}
